import { Link } from "react-router-dom";
import { Mail, Instagram, Facebook } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useLanguageStore } from "@/stores/languageStore";
import baseiyLogo from "@/assets/baseiy-logo.png";

import { INSTAGRAM_URL, FACEBOOK_URL } from "@/lib/socialLinks";

export const Footer = () => {
  const { t } = useLanguageStore();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-charcoal text-white">
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={baseiyLogo} alt="BASE IY logo" className="h-14 w-auto" />
              <span className="font-display text-2xl font-bold text-gold">BASE IY</span>
            </div>
            <p className="text-sm text-white/70">{t("footer.description")}</p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("footer.quickLinks")}</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.home")}
              </Link>
              <Link to="/lp/nagarut" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("footer.ourWorks")}
              </Link>
              <Link to="/lp/wood-table" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("footer.outdoorTable")}
              </Link>
              <Link to="/about" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.about")}
              </Link>
              <Link to="/contact" className="text-sm text-white/70 hover:text-primary transition-colors">
                {t("nav.contact")}
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("footer.contact")}</h3>
            <div className="space-y-3">
              <a href="mailto:onlinecovr2000@gmail.com" className="flex items-center gap-2 text-sm text-white/70 hover:text-primary transition-colors">
                <Mail className="h-4 w-4 text-primary" />
                <span>onlinecovr2000@gmail.com</span>
              </a>
              {/* Social Links */}
              <div className="mt-4 flex items-center gap-3">
                <a 
                  href={INSTAGRAM_URL}
                  className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/20 to-gold/20 border border-primary/30 hover:border-primary/50 hover:bg-primary/30 transition-all group"
                >
                  <Instagram className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                  <span className="text-sm text-white/80 group-hover:text-white transition-colors">{t("home.contactCta")}</span>
                </a>
                <a 
                  href={FACEBOOK_URL}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#1877F2] hover:bg-[#166FE5] transition-all group"
                >
                  <Facebook className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                </a>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h3 className="font-display text-lg font-semibold">{t("footer.newsletterTitle")}</h3>
            <p className="text-sm text-white/70">{t("footer.newsletterSubtitle")}</p>
            <div className="flex gap-2">
              <Input
                placeholder={t("footer.emailPlaceholder")}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
              <Button className="gradient-gold text-white shrink-0">{t("footer.subscribe")}</Button>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-white/10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p>
              © {year} BASE IY. {t("footer.rights")}.
            </p>
            <p className="text-xs text-white/60">
              {t("footer.exemptDealer")}
            </p>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-primary transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-primary transition-colors">
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
